'use client';

import { domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';
import { Fragment } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/app-tooltip';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { MOTION_POSITION_SMALL } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

/**
 * `chart`        — small text pill; rounded shell (no border), subtle indicator.
 *                  Used in chart toolbars (PnL, earnings, market chart controls).
 * `chart-icon`   — icon-only square pill; same shell + indicator as `chart`.
 *                  For chart sub-toggles (line/candle).
 * `chart-filled` — same geometry as `chart`; filled tinted shell (tertiary
 *                  background, no border, no shadow) with a lighter active
 *                  indicator. For compact toggles on solid panels (e.g. the
 *                  launchpad backers panel Top/Recent toggle).
 * `tab`          — large brand-colored tab; no shell. For top-of-list tab rows
 *                  (sort tabs in `SortFilterBar`). Caller controls the
 *                  surrounding flex container.
 * `tab-surface`  — same geometry as `tab`; active segment uses `bg-background-primary`,
 *                  `text-foreground-primary`, and `shadow-sm` (design-system shadow).
 *                  For grouped tabs on secondary page backgrounds (e.g. Social).
 */
export type SegmentedControlVariant =
  | 'chart'
  | 'chart-icon'
  | 'chart-filled'
  | 'tab'
  | 'tab-surface';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface SegmentedControlItem {
  /** Stable key. Used for `key`, `value`, and `aria-controls`. */
  key: string;
  /** Text or node label. Required for `chart` and `tab` variants. */
  label?: ReactNode;
  /** Icon. Required for `chart-icon` variant; rendered as a prefix in `tab` variant. */
  icon?: IconSvgElement;
  /** Extra classes for the rendered icon (e.g. a per-segment size override in `chart-icon`). */
  iconClassName?: string;
  /** Optional tooltip wrapping the trigger. */
  tooltip?: { content: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' };
  /** Disable just this segment (greyed; not clickable). */
  disabled?: boolean;
  /** Accessible label — required when label is omitted (icon-only). */
  ariaLabel?: string;
  /** Hide this segment without removing it from the items array. */
  hidden?: boolean;
}

export interface SegmentedControlProps {
  items: ReadonlyArray<SegmentedControlItem>;
  active: string;
  onChange: (key: string) => void;
  /** Required — unique within the page tree. Drives the motion `layoutId` for the sliding pill. */
  layoutId: string;
  variant?: SegmentedControlVariant;
  /**
   * `vertical` stacks segments in a column (`tab` / `tab-surface` only — full-width triggers).
   * Used for settings-style side nav on desktop; pair with responsive visibility at call sites.
   */
  orientation?: 'horizontal' | 'vertical';
  /** When true, render with `role="tablist"` / `role="tab"` instead of plain buttons. */
  asTabs?: boolean;
  /** Accessible label for the whole control. Lands on the shell as `aria-label`. */
  ariaLabel?: string;
  /** Visually disable the whole control (40% opacity, no pointer events). */
  disabled?: boolean;
  /** Optional — fires when the pointer enters a segment. Used to warm caches before click. */
  onItemPointerEnter?: (key: string) => void;
  /** Stretch the shell across its parent and divide segments evenly (`flex-1`). */
  fullWidth?: boolean;
  /** Size step. Only meaningful for `chart` / `chart-filled` today (bumps padding +
   *  text from caption to body, and the leading icon from 3.5 to 4). Other
   *  variants ignore this prop. */
  size?: 'sm' | 'md';
  /** Additional classes for the active segment's sliding background. */
  indicatorClassName?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------------

// Shell layout: `inline-flex` (content-width) by default; the wrapper appends
// `flex w-full` when `fullWidth` is set so the shell stretches and segments
// can flex-1 to fill it. Display class lives in the shared layout segment.
const SHELL_BY_VARIANT: Record<SegmentedControlVariant, string> = {
  chart: 'items-center gap-0.5 rounded-full bg-background-primary p-1',
  'chart-icon': 'items-center gap-0.5 rounded-full bg-background-primary p-1',
  'chart-filled': 'items-center gap-1 rounded-full bg-background-tertiary p-1',
  tab: 'gap-0',
  'tab-surface': 'gap-0',
};

// Buttons: `shrink-0` keeps each segment to its content width by default;
// when the shell is `fullWidth` we swap it for `flex-1` (handled below).
const BUTTON_BY_VARIANT: Record<SegmentedControlVariant, string> = {
  chart:
    'relative inline-flex items-center justify-center overflow-hidden rounded-full outline-none transition-colors',
  'chart-icon':
    'relative inline-flex size-6 items-center justify-center overflow-hidden rounded-full outline-none transition-colors',
  'chart-filled':
    'relative inline-flex items-center justify-center overflow-hidden rounded-full outline-none transition-colors',
  tab: 'relative appearance-none inline-flex items-center justify-center rounded-pill font-medium whitespace-nowrap outline-none transition-colors h-9 gap-1.5 text-body px-4',
  'tab-surface':
    'relative appearance-none inline-flex items-center justify-center rounded-pill font-medium whitespace-nowrap outline-none transition-colors h-9 gap-1.5 text-body px-4',
};

const ACTIVE_TEXT_BY_VARIANT: Record<SegmentedControlVariant, string> = {
  chart: 'text-foreground-primary',
  'chart-icon': 'text-foreground-primary',
  'chart-filled': 'text-foreground-primary',
  tab: 'text-foreground-brand dark:text-foreground-primary',
  'tab-surface': 'text-foreground-primary',
};

const INACTIVE_TEXT_BY_VARIANT: Record<SegmentedControlVariant, string> = {
  chart: 'text-foreground-secondary hover:text-foreground-primary',
  'chart-icon': 'text-foreground-secondary hover:text-foreground-primary',
  'chart-filled': 'text-foreground-secondary hover:text-foreground-primary',
  tab: 'text-foreground-tertiary hover:text-foreground-primary',
  'tab-surface': 'text-foreground-tertiary hover:text-foreground-primary',
};

const INDICATOR_BY_VARIANT: Record<SegmentedControlVariant, string> = {
  chart:
    'pointer-events-none absolute inset-0 z-0 rounded-full bg-background-secondary shadow-elevation-inset',
  'chart-icon':
    'pointer-events-none absolute inset-0 z-0 rounded-full bg-background-secondary shadow-elevation-inset',
  // Active pill: a step lighter than the shell. Uses a low-opacity
  // foreground-primary overlay so the tone scales correctly across the dark
  // and light themes without reaching for a raw gray primitive.
  'chart-filled': 'pointer-events-none absolute inset-0 z-0 rounded-full bg-foreground-primary/10',
  tab: 'pointer-events-none absolute inset-0 z-0 rounded-pill bg-background-brand dark:bg-background-primary',
  'tab-surface':
    'pointer-events-none absolute inset-0 z-0 rounded-pill bg-background-primary shadow-elevation-inset',
};

const LABEL_INNER_BY_VARIANT: Record<SegmentedControlVariant, string> = {
  chart:
    'relative z-10 inline-flex items-center justify-center px-2.5 py-1 text-caption font-medium',
  'chart-icon': '',
  'chart-filled':
    'relative z-10 inline-flex items-center justify-center px-2 py-1 text-caption font-medium',
  tab: 'relative z-10',
  'tab-surface': 'relative z-10',
};

// `size="md"` override for the two filled-pill variants. Bumps padding +
// text-size; nothing else changes. Other variants are size-locked today.
const LABEL_INNER_MD_BY_VARIANT: Partial<Record<SegmentedControlVariant, string>> = {
  chart: 'relative z-10 inline-flex items-center justify-center px-3 py-1.5 text-body font-medium',
  'chart-filled':
    'relative z-10 inline-flex items-center justify-center px-3 py-1.5 text-body font-medium',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SegmentedControl({
  items,
  active,
  onChange,
  layoutId,
  variant = 'chart',
  orientation = 'horizontal',
  asTabs = false,
  ariaLabel,
  disabled = false,
  onItemPointerEnter,
  fullWidth = false,
  size = 'sm',
  indicatorClassName,
  className,
}: SegmentedControlProps): React.ReactNode {
  const prefersReducedMotion = useReducedMotion();
  const indicatorTransition = prefersReducedMotion ? { duration: 0 } : MOTION_POSITION_SMALL;

  const visibleItems = items.filter((item) => item.hidden !== true);
  const hasTooltips = visibleItems.some((item) => item.tooltip !== undefined);
  const tabLike = variant === 'tab' || variant === 'tab-surface';
  const verticalTabs = orientation === 'vertical' && tabLike;
  const labelInnerClass =
    size === 'md'
      ? (LABEL_INNER_MD_BY_VARIANT[variant] ?? LABEL_INNER_BY_VARIANT[variant])
      : LABEL_INNER_BY_VARIANT[variant];
  const iconSizeClass = size === 'md' ? 'mr-2 size-icon-md' : 'mr-1.5 size-icon-sm';

  const buttonNodes = visibleItems.map((item) => {
    const isActive = active === item.key;
    const itemDisabled = item.disabled === true;
    const button = (
      <m.button
        type="button"
        role={asTabs ? 'tab' : undefined}
        aria-selected={asTabs ? isActive : undefined}
        aria-label={item.ariaLabel}
        disabled={itemDisabled}
        onClick={(): void => {
          if (!itemDisabled) onChange(item.key);
        }}
        onPointerEnter={
          onItemPointerEnter !== undefined && !itemDisabled
            ? (): void => onItemPointerEnter(item.key)
            : undefined
        }
        className={cn(
          BUTTON_BY_VARIANT[variant],
          fullWidth ? 'flex-1' : 'shrink-0',
          verticalTabs && 'w-full justify-start',
          isActive ? ACTIVE_TEXT_BY_VARIANT[variant] : INACTIVE_TEXT_BY_VARIANT[variant],
          itemDisabled && 'opacity-50 pointer-events-none',
          tabLike && item.icon !== undefined && 'pl-3',
        )}
      >
        {isActive && (
          <m.span
            layoutId={layoutId}
            className={cn(INDICATOR_BY_VARIANT[variant], indicatorClassName)}
            transition={indicatorTransition}
          />
        )}
        {variant === 'chart-icon' && item.icon !== undefined && (
          <Icon
            icon={item.icon}
            className={cn('relative z-10 size-icon-sm', item.iconClassName)}
            aria-hidden="true"
          />
        )}
        {tabLike && item.icon !== undefined && (
          <Icon icon={item.icon} className="relative z-10 shrink-0 size-icon-md" />
        )}
        {variant !== 'chart-icon' && item.label !== undefined && (
          <span className={labelInnerClass}>
            {item.icon !== undefined && (variant === 'chart' || variant === 'chart-filled') && (
              <Icon icon={item.icon} className={iconSizeClass} aria-hidden="true" />
            )}
            {item.label}
          </span>
        )}
      </m.button>
    );

    if (item.tooltip !== undefined) {
      return (
        <Tooltip key={item.key}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side={item.tooltip.side ?? 'bottom'}>
            {item.tooltip.content}
          </TooltipContent>
        </Tooltip>
      );
    }
    return <Fragment key={item.key}>{button}</Fragment>;
  });

  // `aria-label` is only valid on an element that carries a role, so it
  // travels with the `tablist` role: when `asTabs` is set the shell is a
  // labelled tablist, otherwise it is a plain (role-less) group and the
  // label is dropped. Bundling both into one typed object keeps the pair
  // coupled for the a11y linter.
  const shellRoleProps: { role: 'tablist'; 'aria-label'?: string } | Record<string, never> = asTabs
    ? { role: 'tablist', ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}) }
    : {};

  const shell = (
    <div
      {...shellRoleProps}
      className={cn(
        fullWidth ? 'flex w-full' : 'inline-flex',
        SHELL_BY_VARIANT[variant],
        verticalTabs && 'min-w-44 flex-col items-stretch',
        disabled && 'opacity-40 pointer-events-none',
        className,
      )}
    >
      {buttonNodes}
    </div>
  );

  const wrapped = hasTooltips ? (
    <TooltipProvider delayDuration={300}>{shell}</TooltipProvider>
  ) : (
    shell
  );

  return <LazyMotion features={domMax}>{wrapped}</LazyMotion>;
}
